import { useAuth } from '@/lib/auth';
import type { TenantSummary } from '@/lib/api/client';

interface TenantBadgeViewProps {
  userEmail: string | null;
  tenants: TenantSummary[];
  activeTenantId: string | null;
  onSwitch: (tenantId: string) => void;
}

const tenantLabel = (t: TenantSummary) => t.name || t.email;

/**
 * Pure presentational core (exported for render tests).
 * - Solo user (only their own tenant): renders nothing.
 * - Viewer with no tenant of their own, one membership: static badge naming
 *   whose journal they're viewing.
 * - Access to 2+ tenants: switchable select showing the active tenant.
 */
export function TenantBadgeView({ userEmail, tenants, activeTenantId, onSwitch }: TenantBadgeViewProps) {
  if (tenants.length === 0) return null;
  const active = tenants.find((t) => t.id === activeTenantId) ?? tenants[0];

  if (tenants.length === 1) {
    // Own tenant only — the common solo case: no clutter.
    if (userEmail != null && tenants[0].email === userEmail) return null;
    // Sole membership on someone else's journal: always show whose book this is.
    return (
      <span
        className="inline-flex items-center rounded-full bg-brand-50 text-brand-700 border border-brand-200 px-2.5 py-1 text-xs font-medium whitespace-nowrap"
        title={`Viewing ${tenantLabel(active)}'s journal (${active.role})`}
      >
        Viewing {tenantLabel(active)}
      </span>
    );
  }

  return (
    <select
      value={active.id}
      onChange={(e) => {
        if (e.target.value && e.target.value !== active.id) onSwitch(e.target.value);
      }}
      aria-label="Active journal"
      title={`Viewing ${tenantLabel(active)}'s journal (${active.role})`}
      className="rounded-full border border-brand-200 bg-brand-50 text-brand-700 px-2.5 py-1 text-xs font-medium focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
    >
      {tenants.map((t) => (
        <option key={t.id} value={t.id}>
          {tenantLabel(t)}
        </option>
      ))}
    </select>
  );
}

export function TenantBadge() {
  const { user, tenants, activeTenantId, switchTenant } = useAuth();
  return (
    <TenantBadgeView
      userEmail={user?.email ?? null}
      tenants={tenants}
      activeTenantId={activeTenantId}
      onSwitch={switchTenant}
    />
  );
}
